import { useState } from "react";
import { AlertTriangle, Download, FileText, Gauge, Plus, Wand2 } from "lucide-react";
import { useIsMobile } from "../social/mobile/MobileChrome";
import { SeoTecnicoMobile } from "./mobile/SeoTecnicoMobile";
import { OCUPADO_TEXTO, fechaCorta, nivelPuntaje, useSeoTecnico, versionTexto, type SeoTecnicoVista } from "./useSeoTecnico";
import "./seoTecnico.css";

/** Informe de SEO técnico para el cliente: fuentes, Lighthouse por tipo de página y versiones. */
export function SeoTecnico({ clientSlug }: { clientSlug: string }) {
  const vista = useSeoTecnico(clientSlug);
  return useIsMobile() ? <SeoTecnicoMobile v={vista} /> : <SeoTecnicoEscritorio v={vista} />;
}

function puntaje(valor: number | null | undefined) {
  return <span className={`seot-score ${nivelPuntaje(valor)}`}>{valor ?? "—"}</span>;
}

function SeoTecnicoEscritorio({ v }: { v: SeoTecnicoVista }) {
  const [ajuste, setAjuste] = useState("");
  const libre = v.ocupado === null;

  return (
    <div className="seot">
      <div className="seot-sources">
        {v.fuentes.map((f) => (
          <div key={f.nombre}>
            <span>{f.nombre}</span>
            <strong>{f.fecha}</strong>
            <small>{f.detalle}</small>
          </div>
        ))}
      </div>

      {v.sinMegaexport && (
        <div className="detail-notice"><AlertTriangle size={16} /><span>Sin megaexport de Semrush para este cliente. Cárgalo en <a href="/semrush">Carga Semrush</a> (Páginas rastreadas con todos los problemas).</span></div>
      )}

      <div className="seot-block">
        <div className="seot-block-head">
          <h3>Tipos de página</h3>
          <p>Una URL de muestra por plantilla; las páginas de utilería (carro, cuenta, búsqueda) quedan fuera. Marca las que quieres medir con Lighthouse.</p>
        </div>
        <div className="seot-table-wrap">
          <table className="seot-table">
            <thead>
              <tr>
                <th aria-label="Medir" />
                <th>Tipo</th>
                <th>URL de muestra</th>
                <th className="num">Páginas</th>
                <th className="num">Rend.</th>
                <th className="num">Acces.</th>
                <th className="num">B. prácticas</th>
                <th className="num">SEO</th>
                <th className="num">LCP</th>
                <th>Medido</th>
              </tr>
            </thead>
            <tbody>
              {v.filas.map((f, i) => (
                <tr key={i}>
                  <td><input type="checkbox" checked={f.marcada} onChange={(e) => v.editarFila(i, { marcada: e.target.checked })} aria-label={`Medir ${f.plantilla}`} /></td>
                  <td><input className="seot-input" value={f.plantilla} onChange={(e) => v.editarFila(i, { plantilla: e.target.value })} placeholder="Tipo" /></td>
                  <td><input className="seot-input seot-url" value={f.url} onChange={(e) => v.editarFila(i, { url: e.target.value })} placeholder="https://" /></td>
                  <td className="num">{f.paginas ?? "—"}</td>
                  <td className="num">{puntaje(f.lighthouse?.performance)}</td>
                  <td className="num">{puntaje(f.lighthouse?.accesibilidad)}</td>
                  <td className="num">{puntaje(f.lighthouse?.buenas_practicas)}</td>
                  <td className="num">{puntaje(f.lighthouse?.seo)}</td>
                  <td className="num">{f.lighthouse?.lcp_s != null ? `${f.lighthouse.lcp_s.toFixed(1)} s` : "—"}</td>
                  <td className="seot-muted">{f.lighthouse ? fechaCorta(f.lighthouse.fecha) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="seot-actions">
          <button className="button button-ghost" type="button" onClick={v.agregarFila} disabled={!libre}><Plus size={14} /> Agregar URL</button>
          <button className="button button-primary" type="button" onClick={v.correrLighthouse} disabled={!libre || !v.seleccion.length}>
            <Gauge size={14} /> Correr Lighthouse ({v.seleccion.length})
          </button>
          <small>Lighthouse móvil vía PageSpeed Insights. Se guarda en BigQuery y alimenta el informe.</small>
        </div>
      </div>

      <div className="seot-block">
        <div className="seot-block-head seot-row">
          <h3>Informe</h3>
          {!!v.informes.length && (
            <select className="seot-select" value={v.version ?? ""} onChange={(e) => v.setVersion(Number(e.target.value))} disabled={!libre}>
              {v.informes.map((i) => <option key={i.version} value={i.version}>{versionTexto(i)}</option>)}
            </select>
          )}
          <div className="seot-row-end">
            {v.urlDescarga && <a className="button button-ghost" href={v.urlDescarga}><Download size={14} /> Descargar HTML</a>}
            <button className="button button-primary" type="button" onClick={v.generar} disabled={!libre || v.sinMegaexport}>
              <FileText size={14} /> {v.informes.length ? "Generar informe nuevo" : "Generar informe"}
            </button>
          </div>
        </div>

        {v.ocupado && <div className="detail-notice"><span className="button-spinner" /><span>{OCUPADO_TEXTO[v.ocupado]}</span></div>}
        {v.error && <div className="detail-notice is-danger"><AlertTriangle size={16} /><span>{v.error}</span></div>}
        {v.aviso && <div className="detail-notice"><span>{v.aviso}</span></div>}

        {v.versionActual?.alertas && (
          <div className="seot-alertas">
            <strong>Dudas para revisar antes de enviar (no van en el informe)</strong>
            <p>{v.versionActual.alertas}</p>
          </div>
        )}

        {v.version != null && (v.html
          ? <iframe className="seot-preview" title="Vista previa del informe" sandbox="" srcDoc={v.html} />
          : <div className="seot-preview seot-preview-empty">Cargando informe…</div>)}

        {v.version != null && (
          <div className="seot-ajuste">
            <label htmlFor="seot-ajuste">Pedir ajuste sobre la versión {v.version}</label>
            <textarea
              id="seot-ajuste"
              value={ajuste}
              onChange={(e) => setAjuste(e.target.value)}
              rows={3}
              maxLength={4000}
              placeholder="Ej.: detalla mejor la solución de la prioridad 2, o explica con un tooltip qué es una URL canónica."
            />
            <button className="button button-primary" type="button" disabled={!libre || !ajuste.trim()}
              onClick={() => void v.ajustar(ajuste.trim()).then((ok) => ok && setAjuste(""))}>
              <Wand2 size={14} /> Aplicar ajuste
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
